import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, History, ImageIcon, Eye, Save, Rocket } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { absoluteMediaUrl } from "@/components/ImageField";

const focalStyle = (d) => `${Number(d?.focal_x ?? 50)}% ${Number(d?.focal_y ?? 40)}%`;

function Frame({ label, url, draft, mobile, testId, defaultNote }) {
  const zoom = Math.min(200, Math.max(100, Number(draft?.zoom) || 100)) / 100;
  return (
    <div data-testid={testId}>
      <span className="text-[10px] tracking-[0.14em] uppercase text-taupe">{label}</span>
      <div className={`mt-2 relative overflow-hidden border border-hair bg-bone ${mobile ? "h-72 w-44" : "h-64 w-full"}`}>
        {url ? (
          <img src={absoluteMediaUrl(url)} alt={draft?.alt || "Anteprima immagine Chi sono"}
            className="w-full h-full object-cover transition-transform"
            style={{ objectPosition: focalStyle(draft), transform: zoom > 1 ? `scale(${zoom})` : "none" }} />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-taupe gap-2">
            <ImageIcon size={20} />
            <span className="text-xs text-center px-2">{defaultNote}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export function AboutImagePanel() {
  const [state, setState] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const deskRef = useRef(null);
  const mobRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/about-image");
      setState(data);
      setDraft(data.draft);
    } catch { toast.error("Errore nel caricamento"); }
  };
  useEffect(() => { load(); }, []);

  if (!state || !draft) return <p className="text-sm text-taupe">Caricamento…</p>;

  const upload = async (target, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/about-image/draft?target=${target}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDraft(data.draft);
      toast.success("Immagine caricata nella bozza — non ancora pubblicata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento");
    } finally { setBusy(false); }
  };

  const saveDraft = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/about-image/draft", {
        alt: draft.alt || "", focal_x: Number(draft.focal_x), focal_y: Number(draft.focal_y), zoom: Number(draft.zoom),
      });
      setDraft(data.draft);
      toast.success("Bozza salvata");
    } catch { toast.error("Errore salvataggio bozza"); }
    finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      await saveDraftSilent();
      await api.post("/admin/about-image/publish");
      toast.success("Immagine pubblicata sul sito");
      load();
    } catch { toast.error("Errore pubblicazione"); }
    finally { setBusy(false); }
  };

  const saveDraftSilent = async () => {
    await api.put("/admin/about-image/draft", {
      alt: draft.alt || "", focal_x: Number(draft.focal_x), focal_y: Number(draft.focal_y), zoom: Number(draft.zoom),
    });
  };

  const restore = async () => {
    setBusy(true);
    try {
      await api.post("/admin/about-image/restore");
      toast.success("Immagine precedente ripristinata");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Nessuna immagine precedente"); }
    finally { setBusy(false); }
  };

  const remove = async (target) => {
    const msg = target === "mobile"
      ? "Eliminare la versione mobile? Verrà usata l'immagine principale."
      : "Eliminare l'immagine della sezione Chi sono? Tornerà quella predefinita del sito.";
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/about-image?target=${target}`);
      toast.success("Immagine eliminata");
      load();
    } catch { toast.error("Errore eliminazione"); }
    finally { setBusy(false); }
  };

  const pub = state.published;
  const dirty = JSON.stringify(draft) !== JSON.stringify(state.published);
  const btn = "min-h-[44px] inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase px-4 py-2 transition-colors disabled:opacity-60";

  return (
    <section className="bg-white border border-hair p-5 md:p-8" data-testid="settings-section-about">
      <div className="eyebrow">Immagine sezione "Chi sono"</div>
      <p className="text-xs text-taupe mt-3">
        Formati: JPG, JPEG, PNG, WEBP · max 10 MB. Le versioni responsive vengono generate automaticamente;
        l'originale resta invariato. Il punto focale evita che il volto venga tagliato su schermi diversi.
      </p>

      {/* Immagine attualmente pubblicata */}
      <div className="mt-6">
        <span className="text-[10px] tracking-[0.14em] uppercase text-taupe">Attualmente pubblicata</span>
        <div className="mt-2 flex items-center gap-4">
          <div className="h-20 w-16 overflow-hidden border border-hair bg-bone shrink-0">
            {pub.image
              ? <img src={absoluteMediaUrl(pub.image)} alt={pub.alt || "Immagine Chi sono pubblicata"} className="w-full h-full object-cover" style={{ objectPosition: focalStyle(pub) }} data-testid="about-published-img" />
              : <div className="w-full h-full flex items-center justify-center text-taupe"><ImageIcon size={16} /></div>}
          </div>
          <div className="text-xs text-taupe" data-testid="about-published-note">
            {pub.image ? "Questa è l'immagine visibile ora sul sito." : "Nessuna immagine personalizzata: il sito mostra quella predefinita."}
            {state.updated_at && <span className="block mt-1">Ultimo aggiornamento: {new Date(state.updated_at).toLocaleString("it-IT")}</span>}
          </div>
        </div>
      </div>

      {/* Anteprima bozza desktop + mobile */}
      <div className="grid md:grid-cols-[1fr_auto] gap-6 mt-8 items-start">
        <Frame label="Anteprima bozza · desktop" url={draft.image} draft={draft} testId="about-preview-desktop" defaultNote="Immagine predefinita del sito" />
        <Frame label="Anteprima bozza · smartphone" url={draft.image_mobile || draft.image} draft={draft} mobile testId="about-preview-mobile" defaultNote="Come desktop" />
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <button onClick={() => deskRef.current?.click()} disabled={busy} className={`${btn} bg-ink text-bone hover:bg-gold`} data-testid="about-upload-desktop">
          <Upload size={14} /> {draft.image ? "Sostituisci immagine" : "Carica immagine"}
        </button>
        <button onClick={() => mobRef.current?.click()} disabled={busy} className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid="about-upload-mobile">
          <Upload size={14} /> Versione smartphone (opzionale)
        </button>
        {state.has_prev && (
          <button onClick={restore} disabled={busy} className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid="about-restore">
            <History size={14} /> Ripristina immagine precedente
          </button>
        )}
        {pub.image && (
          <button onClick={() => remove("all")} disabled={busy} className={`${btn} border border-red-700 text-red-700 hover:bg-red-700 hover:text-bone`} data-testid="about-delete">
            <Trash2 size={14} /> Elimina
          </button>
        )}
        {pub.image_mobile && (
          <button onClick={() => remove("mobile")} disabled={busy} className={`${btn} border border-red-700 text-red-700 hover:bg-red-700 hover:text-bone`} data-testid="about-delete-mobile">
            <Trash2 size={14} /> Elimina mobile
          </button>
        )}
      </div>
      <input ref={deskRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" data-testid="about-file-desktop"
        onChange={(e) => { upload("desktop", e.target.files?.[0]); e.target.value = ""; }} />
      <input ref={mobRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" data-testid="about-file-mobile"
        onChange={(e) => { upload("mobile", e.target.files?.[0]); e.target.value = ""; }} />

      {/* Metadati: alt, punto focale, zoom */}
      <div className="grid md:grid-cols-2 gap-6 mt-8">
        <div className="md:col-span-2">
          <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Testo alternativo (accessibilità e SEO)</Label>
          <Input value={draft.alt || ""} onChange={(e) => setDraft({ ...draft, alt: e.target.value })} className="rounded-none mt-1"
            placeholder="Es. Ritratto di Giovanna Matino nel suo studio" data-testid="about-alt" />
        </div>
        <div>
          <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Punto focale orizzontale: {draft.focal_x}%</Label>
          <input type="range" min={0} max={100} step={5} value={Number(draft.focal_x) || 50}
            onChange={(e) => setDraft({ ...draft, focal_x: Number(e.target.value) })}
            className="w-full mt-4 accent-[#9E8462]" data-testid="about-focal-x" />
        </div>
        <div>
          <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Punto focale verticale: {draft.focal_y}%</Label>
          <input type="range" min={0} max={100} step={5} value={Number(draft.focal_y) ?? 40}
            onChange={(e) => setDraft({ ...draft, focal_y: Number(e.target.value) })}
            className="w-full mt-4 accent-[#9E8462]" data-testid="about-focal-y" />
          <p className="text-[11px] text-taupe mt-1">0% = alto (ideale per non tagliare il volto), 100% = basso.</p>
        </div>
        <div className="md:col-span-2">
          <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Zoom / ritaglio: {draft.zoom}%</Label>
          <input type="range" min={100} max={200} step={5} value={Number(draft.zoom) || 100}
            onChange={(e) => setDraft({ ...draft, zoom: Number(e.target.value) })}
            className="w-full mt-4 accent-[#9E8462]" data-testid="about-zoom" />
          <p className="text-[11px] text-taupe mt-1">Aumenta per riposizionare e ritagliare senza deformare l'immagine.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-8">
        <button onClick={saveDraft} disabled={busy} className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid="about-save-draft">
          <Save size={14} /> Salva bozza
        </button>
        <button disabled className={`${btn} border border-hair text-taupe cursor-default`} data-testid="about-preview-hint" title="L'anteprima sopra si aggiorna in tempo reale">
          <Eye size={14} /> Anteprima in tempo reale sopra
        </button>
        <button onClick={publish} disabled={busy || !dirty} className={`${btn} bg-ink text-bone hover:bg-gold`} data-testid="about-publish">
          <Rocket size={14} /> Salva e pubblica
        </button>
      </div>
      {dirty && <p className="text-[11px] text-gold mt-3" data-testid="about-dirty-note">Ci sono modifiche in bozza non ancora pubblicate sul sito.</p>}
    </section>
  );
}
